// src/components/calendar-view.js
import { getClientBookingsData, sendJobNote, scopeChangeBooking } from '../api/bookings.js';
import { getServices, getCategories } from '../api/services.js';
import { setPreselectedDate, setView } from '../state/store.js';

let allJobs = [];
let catalogServicesList = [];
let catalogCategoriesList = [];
let blockedDatesList = [];

let currentFilter = 'ALL';
let fullCalendarInstance = null;
let activeInspectorJobId = null;

let draftScopeMap = {}; 
let isCatalogExpanded = false;
let inspectorSearchQuery = '';
let inspectorCategoryFilter = 'All';

const STATUS_COLORS = {
  "NewBooking": { bg: "#f59e0b", border: "border-amber-500/30", text: "text-amber-400", pill: "bg-amber-500/10" },
  "Quote needed": { bg: "#f97316", border: "border-orange-500/30", text: "text-orange-400", pill: "bg-orange-500/10" },
  "Booking Set": { bg: "#3b82f6", border: "border-blue-500/30", text: "text-blue-400", pill: "bg-blue-500/10" },
  "In Progress": { bg: "#ec4899", border: "border-pink-500/30", text: "text-pink-400", pill: "bg-pink-500/10" },
  "Has Issue": { bg: "#f43f5e", border: "border-rose-500/30", text: "text-rose-400", pill: "bg-rose-500/10" },
  "Completed": { bg: "#64748b", border: "border-slate-700/50", text: "text-slate-400", pill: "bg-slate-800" },
  "Cancelled": { bg: "#f43f5e", border: "border-rose-500/30", text: "text-rose-400", pill: "bg-rose-500/10" }
};

function refreshCalendarEvents() {
  if (!fullCalendarInstance) return;
  fullCalendarInstance.removeAllEvents();
  fullCalendarInstance.addEventSource(buildCalendarEvents());
}

export function renderCalendarView(container, options = {}) {
  const isListMode = options.initialSubView === 'list';

  container.innerHTML = `
    <div class="space-y-4">
      <!-- HEADER CONTROL BAR -->
      <div class="flex flex-wrap items-center justify-between gap-2.5 bg-slate-900/60 p-3 rounded-2xl border border-slate-800/80">
        <div class="flex flex-wrap gap-1.5" id="status-filters">
          <button data-filter="ALL" class="filter-btn text-xs px-3 py-1.5 rounded-lg bg-slate-800 text-white font-medium border border-slate-700">All Active</button>
          <button data-filter="NewBooking" class="filter-btn text-xs px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-400 font-medium hover:bg-amber-500/20 border border-amber-500/30">New Booking</button>
          <button data-filter="Booking Set" class="filter-btn text-xs px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-400 font-medium hover:bg-sky-500/20 border border-sky-500/30">Booking Set</button>
          <button data-filter="Quote needed" class="filter-btn text-xs px-3 py-1.5 rounded-lg bg-orange-500/10 text-orange-400 font-medium hover:bg-orange-500/20 border border-orange-500/30">Quote Needed</button>
          <button data-filter="In Progress" class="filter-btn text-xs px-3 py-1.5 rounded-lg bg-pink-500/10 text-pink-400 font-medium hover:bg-pink-500/20 border border-pink-500/30">In Progress</button>
        </div>

        <div id="sort-container" class="${isListMode ? 'flex' : 'hidden'} items-center gap-2">
          <span class="text-xs text-slate-400 font-medium">Sort:</span>
          <select id="sort-select" class="bg-slate-950 border border-slate-800 text-xs text-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-500">
            <option value="date-desc">Target Date (Newest First)</option>
            <option value="date-asc">Target Date (Oldest First)</option>
            <option value="id-desc">Job ID (Highest First)</option>
            <option value="quote-desc">Quote Amount (Highest)</option>
          </select>
        </div>
      </div>

      <!-- SHARED JOB INSPECTOR CARD -->
      <div id="calendar-selected-card" class="hidden bg-slate-900 border border-blue-500/40 p-5 rounded-3xl shadow-2xl space-y-4">
        <div class="flex justify-between items-start">
          <div>
            <div id="sel-badge-container" class="flex items-center gap-2 mb-1"></div>
            <h2 id="sel-title" class="text-lg sm:text-xl font-bold text-white"></h2>
            <p id="sel-desc" class="text-xs text-slate-300 mt-1 font-mono"></p>
          </div>
          <button id="close-selected-btn" class="text-slate-400 hover:text-white text-lg p-1">✕</button>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-2.5 bg-slate-950/60 p-3 rounded-2xl border border-slate-800/50 text-xs">
          <div><span class="text-slate-500 block">DB Quote</span><span id="sel-quote" class="font-bold text-emerald-400 text-sm"></span></div>
          <div><span class="text-slate-500 block">Key Safe Code</span><span id="sel-keysafe" class="font-mono text-slate-200"></span></div>
          <div><span class="text-slate-500 block">Invoice Status</span><span id="sel-inv-status" class="font-medium text-slate-200"></span></div>
          <div><span class="text-slate-500 block">Account Contact</span><span id="sel-tenant" class="text-slate-300"></span></div>
        </div>

        <!-- RESCHEDULE CONTROL -->
        <div class="bg-slate-950 p-3.5 rounded-2xl border border-slate-800 space-y-2">
          <span class="text-xs font-bold text-amber-400 flex items-center gap-1.5">
            <span>📅 Self-Service Reschedule</span>
          </span>
          <div class="flex gap-2 items-center">
            <input type="date" id="sel-reschedule-date" class="input-dark text-xs py-1.5 flex-1" />
            <button id="sel-reschedule-btn" class="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs rounded-xl transition">
              Update Execution Date
            </button>
          </div>
        </div>

        <!-- STAGED SCOPE MANAGER & COMMIT BAR -->
        <div class="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
          <div class="flex justify-between items-center">
            <span class="text-xs font-bold text-blue-400 flex items-center gap-1.5">
              <span>🛠️ Active Scope Services (Draft Changes)</span>
            </span>
            <span id="sel-scope-count" class="text-[10px] text-slate-400 font-mono">0 Items in Scope</span>
          </div>

          <!-- INLINE CANCELLATION DROPDOWN PROMPT BANNER -->
          <div id="sel-cancel-prompt-banner" class="hidden p-3 rounded-2xl bg-rose-950/60 border border-rose-500/50 text-xs space-y-2.5 transition-all">
            <div class="flex items-center gap-2 text-rose-300 font-bold">
              <span>⚠️</span>
              <span>Cancel Booking Request?</span>
            </div>
            <p class="text-[11px] text-slate-300 leading-relaxed">
              Removing the final service in scope will leave this booking empty. Would you like to proceed with cancelling this entire booking?
            </p>
            <div class="flex gap-2 pt-1">
              <button id="btn-confirm-prompt-cancel" type="button" class="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-500 text-white font-bold rounded-xl transition text-xs shadow-lg shadow-rose-600/30">
                🚫 Yes, Cancel Booking
              </button>
              <button id="btn-dismiss-prompt-cancel" type="button" class="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold rounded-xl transition text-xs border border-slate-700">
                Keep Service
              </button>
            </div>
          </div>

          <div id="sel-items-list" class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-56 overflow-y-auto pr-1 text-xs"></div>

          <!-- BATCH COMMIT BAR -->
          <div id="sel-commit-bar" class="p-3 bg-blue-950/40 border border-blue-500/30 rounded-xl flex flex-wrap items-center justify-between gap-3">
            <div>
              <span class="text-xs font-bold text-blue-400 block">Staged Scope Balance</span>
              <span id="sel-projected-quote-text" class="text-xs text-slate-300 font-mono">DB: £0.00 ➔ Draft: £0.00</span>
            </div>
            <button id="sel-commit-scope-btn" class="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl transition shadow-lg shadow-blue-600/30 disabled:opacity-40 disabled:cursor-not-allowed">
              🚀 Commit Scope Changes (Single Payload)
            </button>
          </div>
        </div>

        <!-- COLLAPSIBLE ADD CATALOG SERVICES SECTION -->
        <div class="bg-slate-950 p-3.5 rounded-2xl border border-slate-800 space-y-3">
          <button id="sel-toggle-catalog-btn" type="button" class="w-full flex justify-between items-center text-xs font-bold text-slate-300 hover:text-white transition">
            <span class="flex items-center gap-1.5"><span>➕ Add Services from Catalog</span></span>
            <span id="sel-catalog-arrow" class="text-blue-400 font-mono text-[11px]">▸ Expand Catalog</span>
          </button>

          <div id="sel-catalog-drawer" class="hidden space-y-3 pt-2 border-t border-slate-800/80">
            <div class="flex flex-wrap justify-between items-center gap-2">
              <span class="text-xs font-semibold text-slate-400">Filter Services</span>
              <input type="text" id="sel-catalog-search" placeholder="🔍 Search catalog services..." class="input-dark text-xs py-1.5 max-w-xs" />
            </div>

            <div class="flex gap-1.5 overflow-x-auto pb-1" id="sel-category-pills"></div>

            <div id="sel-catalog-grid" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto pr-1"></div>
          </div>
        </div>

        <!-- NOTES & FIELD LOGS -->
        <div class="grid md:grid-cols-2 gap-2.5 text-xs">
          <div class="bg-slate-950 p-3 rounded-xl border border-slate-800">
            <span class="text-blue-400 font-semibold block mb-1">Major Maker Field Note:</span>
            <p id="sel-mm-note" class="text-slate-300 italic"></p>
          </div>
          <div class="bg-slate-950 p-3 rounded-xl border border-slate-800">
            <span class="text-emerald-400 font-semibold block mb-1">Client Note:</span>
            <p id="sel-ppj-note" class="text-slate-300 italic"></p>
          </div>
        </div>

        <div id="sel-actions" class="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-800/80"></div>
      </div>

      <!-- CALENDAR VIEW -->
      <div id="calendar-container" class="${isListMode ? 'hidden' : 'block'} space-y-4">
        <div class="bg-slate-900/60 p-3 sm:p-4 rounded-3xl border border-slate-800/80 shadow-xl min-h-[500px]">
          <div id="fullcalendar-mount"></div>
        </div>
      </div>

      <!-- LIST VIEW -->
      <div id="list-container" class="${isListMode ? 'block' : 'hidden'} space-y-3">
        <div id="list-rows" class="space-y-3">
          <!-- PULSING LOADING SKELETONS -->
          <div class="p-4 bg-slate-900/40 border border-slate-800 rounded-2xl animate-pulse space-y-2">
            <div class="h-4 bg-slate-800 rounded w-1/4"></div>
            <div class="h-6 bg-slate-800 rounded w-3/4"></div>
            <div class="h-3 bg-slate-800/60 rounded w-1/2"></div>
          </div>
          <div class="p-4 bg-slate-900/40 border border-slate-800 rounded-2xl animate-pulse space-y-2">
            <div class="h-4 bg-slate-800 rounded w-1/4"></div>
            <div class="h-6 bg-slate-800 rounded w-3/4"></div>
            <div class="h-3 bg-slate-800/60 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    </div>
  `;

  if (!isListMode) {
    initFullCalendar(container);
  }

  // Hydrate Data with Stale-While-Revalidate Fast Cache
  Promise.all([
    getClientBookingsData(),
    getServices().catch(() => []),
    getCategories().catch(() => ['All'])
  ]).then(([{ jobs, blockedDates }, services, categories]) => {
    allJobs = jobs || [];
    blockedDatesList = blockedDates || [];
    catalogServicesList = services || [];
    catalogCategoriesList = categories || ['All'];

    refreshCalendarEvents();
    renderListView(container);
  }).catch(err => {
    console.error('Failed to load client bookings:', err);
  });

  // Filter Handlers
  container.querySelectorAll('.filter-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      currentFilter = e.target.dataset.filter;
      container.querySelectorAll('.filter-btn').forEach(b => {
        const isActive = b.dataset.filter === currentFilter;
        b.classList.toggle('bg-slate-800', isActive);
        b.classList.toggle('text-white', isActive);
      });
      refreshCalendarEvents();
      renderListView(container);
    });
  });

  const sortSelect = container.querySelector('#sort-select');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => renderListView(container));
  }

  container.querySelector('#close-selected-btn').addEventListener('click', () => {
    activeInspectorJobId = null;
    container.querySelector('#calendar-selected-card').classList.add('hidden');
    document.querySelectorAll('.fc-event').forEach(el => el.classList.remove('selected-event'));
  });
}

function initFullCalendar(container) {
  const calEl = container.querySelector('#fullcalendar-mount');
  if (!calEl) return;

  if (fullCalendarInstance) {
    fullCalendarInstance.destroy();
  }

  fullCalendarInstance = new FullCalendar.Calendar(calEl, {
    initialView: 'dayGridMonth',
    headerToolbar: { left: 'prev,next today', center: 'title', right: '' },
    aspectRatio: window.innerWidth < 768 ? 1.0 : 1.8,
    dayMaxEvents: 2,
    events: buildCalendarEvents(),

    dayCellDidMount: (info) => {
      const y = info.date.getFullYear();
      const m = String(info.date.getMonth() + 1).padStart(2, '0');
      const d = String(info.date.getDate()).padStart(2, '0');
      const cellDateStr = `${y}-${m}-${d}`;
      const todayStr = new Date().toISOString().split('T')[0];

      if (cellDateStr >= todayStr && !blockedDatesList.includes(cellDateStr)) {
        const topEl = info.el.querySelector('.fc-daygrid-day-top');
        if (topEl && !topEl.querySelector('.btn-add-booking-cell')) {
          const plusBtn = document.createElement('button');
          plusBtn.className = 'btn-add-booking-cell';
          plusBtn.type = 'button';
          plusBtn.innerHTML = '+';
          plusBtn.title = `Book service for ${cellDateStr}`;

          plusBtn.onclick = (e) => {
            e.stopPropagation();
            setPreselectedDate(cellDateStr);
            setView('booking');
          };

          topEl.appendChild(plusBtn);
        }
      }
    },

    dateClick: (info) => {
      const todayStr = new Date().toISOString().split('T')[0];
      if (info.dateStr < todayStr || blockedDatesList.includes(info.dateStr)) return;
      setPreselectedDate(info.dateStr);
      setView('booking');
    },

    eventClick: (info) => {
      if (info.event.display === 'background') return;
      document.querySelectorAll('.fc-event').forEach(el => el.classList.remove('selected-event'));
      if (info.el) info.el.classList.add('selected-event');
      selectJob(container, info.event.id);
    }
  });

  fullCalendarInstance.render();
  setTimeout(() => fullCalendarInstance?.updateSize(), 50);
}

function getFilteredJobs() {
  if (currentFilter === 'ALL') return allJobs;
  return allJobs.filter(j => j.status === currentFilter);
}

function buildCalendarEvents() {
  const events = [];

  blockedDatesList.forEach(dateStr => {
    events.push({
      start: dateStr,
      display: 'background',
      color: '#1e293b',
      classNames: ['shadowed-busy-day']
    });
  });

  getFilteredJobs()
    .filter(j => j.target_date)
    .forEach(j => {
      events.push({
        id: j.id,
        title: `#${j.id} - ${j.location}`,
        start: j.target_date,
        backgroundColor: STATUS_COLORS[j.status]?.bg || '#3b82f6'
      });
    });

  return events;
}

function parseItemsWithQuantities(job) {
  const items = job.items || [];
  const desc = job.description || "";

  return items.map(itm => {
    let qty = Number(itm.quantity) || 1;
    if (qty === 1 && desc) {
      const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapeRegex(itm.name) + "\\s*\\(x(\\d+(?:\\.\\d+)?)\\)", "i");
      const match = desc.match(regex);
      if (match) {
        qty = Number(match[1]);
      }
    }
    return {
      ...itm,
      quantity: qty
    };
  });
}

async function selectJob(container, jobId) {
  activeInspectorJobId = jobId;
  const job = allJobs.find(j => j.id == jobId);
  if (!job) return;

  const parsedItems = parseItemsWithQuantities(job);
  draftScopeMap = {};

  parsedItems.forEach(itm => {
    const match = catalogServicesList.find(cs => cs.name === itm.name);
    draftScopeMap[itm.name] = {
      id: itm.id,
      name: itm.name,
      price: Number(itm.price) || (match ? match.priceTotal : 0),
      quantity: itm.quantity,
      initialQuantity: itm.quantity
    };
  });

  const card = container.querySelector('#calendar-selected-card');
  const color = STATUS_COLORS[job.status] || STATUS_COLORS['NewBooking'];
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.location)}`;

  container.querySelector('#sel-badge-container').innerHTML = `
    <span class="text-xs font-mono bg-blue-500/20 text-blue-400 px-2.5 py-0.5 rounded-full font-bold">Job #${job.id}</span>
    <span class="text-xs px-2 py-0.5 rounded-lg font-semibold ${color.pill} ${color.text}">${job.status}</span>
    <span class="text-xs text-slate-400 font-medium">${job.target_date || 'Unscheduled'}</span>
  `;

  container.querySelector('#sel-title').textContent = job.location;
  container.querySelector('#sel-desc').textContent = job.description;
  container.querySelector('#sel-quote').textContent = `£${Number(job.quote || 0).toFixed(2)}`;
  container.querySelector('#sel-keysafe').textContent = job.key_safe || 'N/A';
  container.querySelector('#sel-inv-status').textContent = job.invoice_status || 'Open Job';
  container.querySelector('#sel-tenant').textContent = `${job.customer_name || 'N/A'} (${job.customer_phone || 'N/A'})`;

  container.querySelector('#sel-mm-note').textContent = job.mm_note || 'No field notes provided yet.';
  container.querySelector('#sel-ppj-note').textContent = job.ppj_message || 'No notes attached.';
  container.querySelector('#sel-reschedule-date').value = job.target_date || '';

  // Hide cancellation prompt banner on job selection
  container.querySelector('#sel-cancel-prompt-banner')?.classList.add('hidden');

  const toggleCatalogBtn = container.querySelector('#sel-toggle-catalog-btn');
  const catalogDrawer = container.querySelector('#sel-catalog-drawer');
  const catalogArrow = container.querySelector('#sel-catalog-arrow');

  isCatalogExpanded = false;
  catalogDrawer.classList.add('hidden');
  catalogArrow.textContent = '▸ Expand Catalog';

  toggleCatalogBtn.onclick = () => {
    isCatalogExpanded = !isCatalogExpanded;
    catalogDrawer.classList.toggle('hidden', !isCatalogExpanded);
    catalogArrow.textContent = isCatalogExpanded ? '▾ Collapse Catalog' : '▸ Expand Catalog';
  };

  updateInspectorDraftUI(container, job);

  // Reschedule Button Handler
  container.querySelector('#sel-reschedule-btn').onclick = async () => {
    const newDate = container.querySelector('#sel-reschedule-date').value;
    if (!newDate) return alert("Select a valid target date.");

    try {
      await scopeChangeBooking(job.id, { target_date: `${newDate} 09:00:00+00:00` });
      alert(`Booking #${job.id} rescheduled to ${newDate}!`);
      await refreshJobsData(container);
    } catch (err) {
      alert(`Reschedule Error: ${err.message}`);
    }
  };

  // Single Batch Commit Button Handler
  container.querySelector('#sel-commit-scope-btn').onclick = async () => {
    const payload = buildScopeChangePayload();
    if (payload.items_to_add.length === 0 && payload.items_to_remove.length === 0) {
      return alert("No scope modifications detected in draft.");
    }

    const commitBtn = container.querySelector('#sel-commit-scope-btn');
    try {
      commitBtn.disabled = true;
      commitBtn.textContent = '🚀 Executing Single Scope Payload...';

      const res = await scopeChangeBooking(job.id, payload);
      alert(`Scope changes successfully committed for Booking #${job.id}! Quote synced to £${res.jobQuote || '0.00'}`);
      await refreshJobsData(container);
    } catch (err) {
      alert(`Commit Scope Error: ${err.message}`);
    } finally {
      commitBtn.disabled = false;
      commitBtn.textContent = '🚀 Commit Scope Changes (Single Payload)';
    }
  };

  // Action Bar Controls
  container.querySelector('#sel-actions').innerHTML = `
    <div class="flex flex-wrap gap-2 w-full justify-between items-center">
      <div class="flex gap-2">
        <a href="${mapsUrl}" target="_blank" class="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-blue-400 rounded-xl transition border border-slate-700">📍 Directions</a>
        <button id="sel-cancel-job-btn" class="px-3 py-2 bg-rose-600/20 hover:bg-rose-600 text-rose-300 hover:text-white text-xs font-semibold rounded-xl transition border border-rose-500/30">
          🚫 Cancel Booking
        </button>
      </div>
      <div class="flex gap-2 flex-1 max-w-sm">
        <input type="text" id="drawer-note-input" class="input-dark text-xs py-1.5" placeholder="Add note to operator..." />
        <button id="drawer-note-btn" class="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white rounded-xl transition">Send</button>
      </div>
    </div>
  `;

  container.querySelector('#drawer-note-btn').onclick = async () => {
    const input = container.querySelector('#drawer-note-input');
    if (!input.value.trim()) return;
    await sendJobNote(job.id, input.value.trim());
    alert('Note attached to job!');
    input.value = '';
  };

  const handleCancelBooking = async () => {
    try {
      await scopeChangeBooking(job.id, { cancel_booking: true });
      alert(`Booking #${job.id} cancelled.`);
      await refreshJobsData(container);
    } catch (err) {
      alert(`Cancellation Error: ${err.message}`);
    }
  };

  container.querySelector('#sel-cancel-job-btn').onclick = () => {
    container.querySelector('#sel-cancel-prompt-banner')?.classList.remove('hidden');
  };

  container.querySelector('#btn-confirm-prompt-cancel').onclick = handleCancelBooking;
  container.querySelector('#btn-dismiss-prompt-cancel').onclick = () => {
    container.querySelector('#sel-cancel-prompt-banner')?.classList.add('hidden');
  };

  card.classList.remove('hidden');
  card.scrollIntoView({ behavior: 'smooth' });
}

function buildScopeChangePayload() {
  const items_to_add = [];
  const items_to_remove = [];

  Object.values(draftScopeMap).forEach(itm => {
    if (itm.quantity > 0) {
      if (itm.quantity !== itm.initialQuantity || itm.initialQuantity === 0) {
        items_to_add.push({ service_name: itm.name, quantity: itm.quantity });
      }
    } else if (itm.initialQuantity > 0) {
      if (itm.id) {
        items_to_remove.push(Number(itm.id));
      }
    }
  });

  return { items_to_add, items_to_remove };
}

function updateInspectorDraftUI(container, job) {
  renderActiveDraftScopeList(container, job);
  renderInspectorCatalogGrid(container, job);

  let projectedTotal = 0;
  Object.values(draftScopeMap).forEach(itm => {
    if (itm.quantity > 0) {
      projectedTotal += (itm.price * itm.quantity);
    }
  });

  const dbQuote = Number(job.quote || 0);
  const textEl = container.querySelector('#sel-projected-quote-text');
  const commitBtn = container.querySelector('#sel-commit-scope-btn');

  const payload = buildScopeChangePayload();
  const hasChanges = payload.items_to_add.length > 0 || payload.items_to_remove.length > 0;

  if (textEl) {
    textEl.innerHTML = `DB: <span class="font-bold text-slate-300">£${dbQuote.toFixed(2)}</span> ➔ Draft: <span class="font-bold ${hasChanges ? 'text-blue-400' : 'text-emerald-400'}">£${projectedTotal.toFixed(2)}</span>`;
  }

  if (commitBtn) {
    commitBtn.disabled = !hasChanges;
    commitBtn.textContent = hasChanges
      ? `🚀 Commit Scope Changes (${payload.items_to_add.length + payload.items_to_remove.length} Edits Staged)`
      : `🚀 Commit Scope Changes (No Edits Staged)`;
  }
}

function renderActiveDraftScopeList(container, job) {
  const itemsContainer = container.querySelector('#sel-items-list');
  const activeEntries = Object.values(draftScopeMap).filter(itm => itm.quantity > 0);

  container.querySelector('#sel-scope-count').textContent = `${activeEntries.length} Active Services`;

  if (activeEntries.length === 0) {
    itemsContainer.innerHTML = `<div class="col-span-full text-slate-500 italic p-3 text-center bg-slate-900 rounded-xl">No active services in draft scope. Add services from the catalog below.</div>`;
    return;
  }

  itemsContainer.innerHTML = activeEntries.map(itm => {
    const match = catalogServicesList.find(cs => cs.name === itm.name);
    const imgUrl = match?.imageUrl;
    const lineTotal = itm.price * itm.quantity;

    return `
      <div class="bg-slate-900 p-3 rounded-2xl border border-slate-800 flex items-center justify-between gap-3">
        <div class="flex items-center gap-2.5 min-w-0">
          ${imgUrl ? `
            <img src="${imgUrl}" alt="${itm.name}" class="w-10 h-12 rounded-lg object-cover border border-slate-800 flex-shrink-0" />
          ` : `
            <div class="w-10 h-10 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-center text-sm flex-shrink-0">🛠️</div>
          `}
          <div class="min-w-0">
            <h4 class="font-bold text-xs text-white truncate">${itm.name}</h4>
            <span class="text-[10px] text-emerald-400 font-mono block">£${lineTotal.toFixed(2)} (£${itm.price.toFixed(2)}/unit)</span>
          </div>
        </div>

        <div class="flex items-center gap-1.5 flex-shrink-0">
          <button class="btn-scope-dec w-6 h-6 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-lg flex items-center justify-center text-xs" data-name="${itm.name}">-</button>
          <span class="text-xs font-mono font-bold text-blue-400 px-1">x${itm.quantity}</span>
          <button class="btn-scope-inc w-6 h-6 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-lg flex items-center justify-center text-xs" data-name="${itm.name}">+</button>
          <button class="btn-scope-remove px-2 py-1 bg-rose-600/20 hover:bg-rose-600 text-rose-300 hover:text-white border border-rose-500/30 rounded-lg transition text-[10px] font-bold ml-1" data-name="${itm.name}">
            ✕ Remove
          </button>
        </div>
      </div>
    `;
  }).join('');

  // Safeguard Decrement / Remove Handlers for 1-Item Limit
  itemsContainer.querySelectorAll('.btn-scope-inc').forEach(btn => {
    btn.onclick = (e) => {
      const name = e.currentTarget.dataset.name;
      if (draftScopeMap[name]) {
        draftScopeMap[name].quantity += 1;
        updateInspectorDraftUI(container, job);
      }
    };
  });

  itemsContainer.querySelectorAll('.btn-scope-dec').forEach(btn => {
    btn.onclick = (e) => {
      const name = e.currentTarget.dataset.name;
      if (draftScopeMap[name]) {
        const activeCount = Object.values(draftScopeMap).filter(i => i.quantity > 0).length;
        
        // Unfold visual inline cancellation banner if decrementing the sole active service
        if (draftScopeMap[name].quantity === 1 && activeCount === 1) {
          container.querySelector('#sel-cancel-prompt-banner')?.classList.remove('hidden');
          container.querySelector('#sel-cancel-prompt-banner')?.scrollIntoView({ behavior: 'smooth' });
          return;
        }

        draftScopeMap[name].quantity = Math.max(0, draftScopeMap[name].quantity - 1);
        updateInspectorDraftUI(container, job);
      }
    };
  });

  itemsContainer.querySelectorAll('.btn-scope-remove').forEach(btn => {
    btn.onclick = (e) => {
      const name = e.currentTarget.dataset.name;
      if (draftScopeMap[name]) {
        const activeCount = Object.values(draftScopeMap).filter(i => i.quantity > 0).length;

        // Unfold visual inline cancellation banner if removing the sole active service
        if (activeCount === 1) {
          container.querySelector('#sel-cancel-prompt-banner')?.classList.remove('hidden');
          container.querySelector('#sel-cancel-prompt-banner')?.scrollIntoView({ behavior: 'smooth' });
          return;
        }

        draftScopeMap[name].quantity = 0;
        updateInspectorDraftUI(container, job);
      }
    };
  });
}

function renderInspectorCatalogGrid(container, job) {
  const pillsContainer = container.querySelector('#sel-category-pills');
  const catalogGrid = container.querySelector('#sel-catalog-grid');
  const searchInput = container.querySelector('#sel-catalog-search');

  if (!pillsContainer || !catalogGrid) return;

  pillsContainer.innerHTML = catalogCategoriesList.map(cat => `
    <button type="button" data-category="${cat}" class="sel-cat-pill text-[11px] px-2.5 py-1 rounded-lg ${cat === inspectorCategoryFilter ? 'bg-blue-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'} transition border border-slate-800 whitespace-nowrap">
      ${cat}
    </button>
  `).join('');

  pillsContainer.querySelectorAll('.sel-cat-pill').forEach(pill => {
    pill.onclick = (e) => {
      inspectorCategoryFilter = e.currentTarget.dataset.category;
      renderInspectorCatalogGrid(container, job);
    };
  });

  searchInput.value = inspectorSearchQuery;
  searchInput.oninput = (e) => {
    inspectorSearchQuery = e.target.value.toLowerCase().trim();
    renderInspectorCatalogGrid(container, job);
  };

  const filteredServices = catalogServicesList.filter(s => {
    const matchCat = inspectorCategoryFilter === 'All' || s.category === inspectorCategoryFilter;
    const matchSearch = !inspectorSearchQuery || s.name.toLowerCase().includes(inspectorSearchQuery);
    return matchCat && matchSearch;
  });

  if (filteredServices.length === 0) {
    catalogGrid.innerHTML = `<div class="col-span-full p-4 text-center text-slate-500 text-xs">No catalog services match search query.</div>`;
    return;
  }

  catalogGrid.innerHTML = filteredServices.map(service => {
    const activeDraft = draftScopeMap[service.name];
    const activeQty = activeDraft ? activeDraft.quantity : 0;

    return `
      <div class="bg-slate-900/90 p-3 rounded-2xl border ${activeQty > 0 ? 'border-blue-500/60 bg-blue-950/20' : 'border-slate-800'} flex flex-col justify-between space-y-2 transition">
        <div class="flex gap-2.5 items-start">
          ${service.imageUrl ? `
            <img src="${service.imageUrl}" alt="${service.name}" class="w-10 h-10 rounded-lg object-cover border border-slate-800 flex-shrink-0" />
          ` : `
            <div class="w-10 h-10 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-center text-sm flex-shrink-0">🛠️</div>
          `}
          <div class="space-y-0.5 flex-1 min-w-0">
            <h4 class="font-bold text-xs text-white truncate">${service.name}</h4>
            <p class="text-[10px] text-slate-400 line-clamp-2">${service.description || 'Standard service item.'}</p>
          </div>
        </div>

        <div class="flex items-center justify-between pt-2 border-t border-slate-800/80">
          <span class="text-xs font-bold text-emerald-400">£${Number(service.priceTotal).toFixed(2)}</span>
          <div class="flex items-center gap-1.5">
            ${activeQty > 0 ? `
              <span class="text-[10px] font-mono font-bold text-blue-400 px-1">In Scope (x${activeQty})</span>
            ` : ''}
            <button type="button" data-name="${service.name}" data-price="${service.priceTotal}" class="btn-add-cat-item px-2.5 py-1 rounded-lg ${activeQty > 0 ? 'bg-blue-600 text-white' : 'bg-slate-800 text-blue-400 hover:bg-slate-700'} text-xs font-semibold transition">
              ${activeQty > 0 ? '+ More' : '+ Add'}
            </button>
          </div>
        </div>
      </div>
    `;
  }).join('');

  catalogGrid.querySelectorAll('.btn-add-cat-item').forEach(btn => {
    btn.onclick = (e) => {
      const sName = e.currentTarget.dataset.name;
      const sPrice = Number(e.currentTarget.dataset.price) || 0;

      if (!draftScopeMap[sName]) {
        draftScopeMap[sName] = {
          id: null,
          name: sName,
          price: sPrice,
          quantity: 0,
          initialQuantity: 0
        };
      }

      draftScopeMap[sName].quantity += 1;
      updateInspectorDraftUI(container, job);
    };
  });
}

async function refreshJobsData(container) {
  const [{ jobs, blockedDates }, services] = await Promise.all([
    getClientBookingsData(true),
    getServices().catch(() => [])
  ]);

  allJobs = jobs || [];
  blockedDatesList = blockedDates || [];
  catalogServicesList = services || [];

  refreshCalendarEvents();
  renderListView(container);

  if (activeInspectorJobId) {
    selectJob(container, activeInspectorJobId);
  }
}

function renderListView(container) {
  const listRows = container.querySelector('#list-rows');
  if (!listRows) return;

  let jobs = getFilteredJobs();
  const sortType = container.querySelector('#sort-select')?.value || 'date-desc';

  jobs.sort((a, b) => {
    if (sortType === 'date-desc') return new Date(b.target_date || 0) - new Date(a.target_date || 0);
    if (sortType === 'date-asc') return new Date(a.target_date || 0) - new Date(b.target_date || 0);
    if (sortType === 'id-desc') return b.id - a.id;
    if (sortType === 'quote-desc') return (b.quote || 0) - (a.quote || 0);
  });

  if (!jobs.length) {
    listRows.innerHTML = `<div class="text-center p-8 text-slate-500 text-xs">No active bookings match the selected filter.</div>`;
    return;
  }

  listRows.innerHTML = jobs.map(j => {
    const color = STATUS_COLORS[j.status] || STATUS_COLORS['NewBooking'];
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(j.location)}`;

    return `
      <div class="list-job-card bg-slate-900/80 p-4 sm:p-5 rounded-2xl border ${color.border} space-y-3 hover:border-blue-500/50 transition cursor-pointer" data-id="${j.id}">
        <div class="flex justify-between items-start gap-3">
          <div class="space-y-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-xs font-mono bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded font-bold">#${j.id}</span>
              <span class="text-xs text-slate-400">${j.target_date || 'Unscheduled'}</span>
            </div>
            <h3 class="font-bold text-base text-white">${j.location}</h3>
            <p class="text-xs text-slate-300 line-clamp-2">${j.description}</p>
          </div>
          <div class="text-right flex-shrink-0">
            <span class="text-xs px-2.5 py-1 rounded-lg font-semibold ${color.pill} ${color.text} inline-block mb-1">${j.status}</span>
            <div class="text-sm font-bold text-emerald-400">£${Number(j.quote || 0).toFixed(2)}</div>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-3 gap-2 bg-slate-950/60 p-2.5 rounded-xl border border-slate-800/50 text-xs">
          <div><span class="text-slate-500">Key Safe:</span> <span class="text-slate-200 font-mono">${j.key_safe || 'N/A'}</span></div>
          <div><span class="text-slate-500">Account:</span> <span class="text-slate-300">${j.customer_name || 'N/A'}</span></div>
          <div><span class="text-slate-500">Invoice:</span> <span class="text-slate-200 font-medium">${j.invoice_status || 'Open Job'}</span></div>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-800">
          <button class="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600 text-xs font-semibold text-blue-400 hover:text-white rounded-lg transition border border-blue-500/30">
            ⚙️ Expand Scope & Reschedule
          </button>
          <a href="${mapsUrl}" target="_blank" onclick="event.stopPropagation();" class="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-blue-400 rounded-lg transition border border-slate-700">📍 Directions</a>
        </div>
      </div>
    `;
  }).join('');

  listRows.querySelectorAll('.list-job-card').forEach(card => {
    card.onclick = () => {
      selectJob(container, card.dataset.id);
    };
  });
}